// orchestrator_fullstack/frontend/src/app/auth/AuthProvider.tsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  AUTH_ME_CACHE_KEY,
  AUTH_ME_TTL_MS,
  AUTH_STORAGE_KEY,
  AuthMe,
  AuthState,
  deriveFlags,
  normalizeRoles,
  Role,
} from "./rbac";
import { ApiError, fetchAuthMe, setToken as persistToken } from "./api";

type AuthContextValue = {
  state: AuthState;
  flags: ReturnType<typeof deriveFlags>;

  // UX helpers for guards/UI
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  refresh: () => Promise<void>;
  signInWithToken: (token: string) => Promise<void>;
  signOut: () => void;

  // Optional helper (handy for tests / future flows)
  setToken: (token: string | null) => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

function loadCachedAuthMe(): { me: AuthMe; fetchedAt: number } | null {
  try {
    const raw = localStorage.getItem(AUTH_ME_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (!parsed.me || !parsed.fetchedAt) return null;
    return { me: parsed.me as AuthMe, fetchedAt: Number(parsed.fetchedAt) };
  } catch {
    return null;
  }
}

function saveCachedAuthMe(me: AuthMe, fetchedAt: number) {
  try {
    localStorage.setItem(AUTH_ME_CACHE_KEY, JSON.stringify({ me, fetchedAt }));
  } catch {
    // ignore
  }
}

function clearCachedAuthMe() {
  try {
    localStorage.removeItem(AUTH_ME_CACHE_KEY);
  } catch {
    // ignore
  }
}

function loadToken(): string | null {
  const t = localStorage.getItem(AUTH_STORAGE_KEY);
  return t && t.trim() ? t.trim() : null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(true);

  const [state, setState] = useState<AuthState>(() => {
    const token = loadToken();
    const cached = loadCachedAuthMe();
    if (token && cached && Date.now() - cached.fetchedAt < AUTH_ME_TTL_MS) {
      const roles = normalizeRoles(cached.me.roles);
      return {
        token,
        user: cached.me.user,
        tenant: cached.me.tenant,
        roles,
        claims: cached.me.claims || {},
        fetchedAt: cached.fetchedAt,
        needsProvisioning: false,
      };
    }
    return { token, user: null, tenant: null, roles: [], claims: {}, fetchedAt: null, needsProvisioning: false, };
  });

  const flags = useMemo(() => deriveFlags(state.roles), [state.roles]);

  const isAuthenticated = useMemo(() => {
    // If JWT exists but /auth/me has not populated user yet, treat as not authenticated
    return !!state.token && !!state.user;
  }, [state.token, state.user]);

  const signOut = useCallback(() => {
    persistToken(null);
    clearCachedAuthMe();
    setState({ token: null, user: null, tenant: null, roles: [], claims: {}, fetchedAt: null, needsProvisioning: false, });
  }, []);

  const setToken = useCallback(
    (token: string | null) => {
      persistToken(token);
      if (!token) {
        // hard sign out semantics
        signOut();
      } else {
        // keep state.token in sync immediately; refresh will fill the rest
        setState((s) => ({ ...s, token }));
      }
    },
    [signOut]
  );

  const refresh = useCallback(async () => {
    const token = loadToken();
    if (!token) {
      setState((s) => ({
        ...s,
        token: null,
        user: null,
        tenant: null,
        roles: [],
        claims: {},
        fetchedAt: null,
        needsProvisioning: false,
      }));
      return;
    }

    try {
      const me = await fetchAuthMe();
      const roles = normalizeRoles(me.roles);
      const fetchedAt = Date.now();
      saveCachedAuthMe(me, fetchedAt);
      setState({
        token,
        user: me.user,
        tenant: me.tenant,
        roles,
        claims: me.claims || {},
        fetchedAt,
        needsProvisioning: false,
      });
    } catch (e: any) {
      // Backend is source of truth:
      // 401 => sign out (token invalid/expired)
      if (e instanceof ApiError && e.status === 401) {
        signOut();
        return;
      }
      // 403 on /auth/me: treat as unauthenticated (token present but not accepted by server)
      //if (e instanceof ApiError && e.status === 403) {
      //  signOut();
      //  return;
      //}
      // 403 on /auth/me:
      // - If NO_MEMBERSHIP => keep token and mark provisioning required
      // - Otherwise sign out (token present but not accepted by server)
      
      if (e instanceof ApiError && e.status === 403) {
        // FastAPI returns {"detail": {...}}.
        // Depending on ApiError implementation, parsed JSON may be on e.body.
        const body: any = (e as any).body;
        const detail: any =
          (e as any).detail ??          // if ApiError provides it
          body?.detail ??               // typical
          body ??                       // fallback
          null;

        const code = detail && typeof detail === "object" ? String(detail.code || "") : "";

        if (code === "NO_MEMBERSHIP") {
          setState((s) => ({
            ...s,
            token,
            user: s.user || null,
            tenant: null,
            roles: [],
            claims: s.claims || {},
            fetchedAt: null,
            needsProvisioning: true,
          }));
          return;
        }

        signOut();
        return;
      }
      // Otherwise keep token but clear identity; user can retry
      setState((s) => ({ ...s, user: null, tenant: null, roles: [], claims: {}, fetchedAt: null, needsProvisioning: false, }));
    }
  }, [signOut]);

  const signInWithToken = useCallback(
    async (token: string) => {
      persistToken(token);
      // make token visible immediately
      setState((s) => ({ ...s, token }));
      await refresh();
    },
    [refresh]
  );

  useEffect(() => {
    // Revalidate on app load (even if cached) with TTL protection:
    // - if cached is fresh, we still do a best-effort refresh in background
    // - but we can avoid blocking initial render
    let cancelled = false;

    (async () => {
      try {
        setIsLoading(true);

        const token = loadToken();
        if (!token) {
          if (!cancelled) {
            setState((s) => ({
              ...s,
              token: null,
              user: null,
              tenant: null,
              roles: [],
              claims: {},
              fetchedAt: null,
              needsProvisioning: false,
            }));
          }
          return;
        }

        const cached = loadCachedAuthMe();
        const isFresh = !!cached && Date.now() - cached.fetchedAt < AUTH_ME_TTL_MS;

        if (!isFresh) {
          await refresh();
        } else {
          // refresh in background (do not block)
          refresh().catch(() => {});
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const value: AuthContextValue = useMemo(
    () => ({
      state,
      flags,
      isAuthenticated,
      isLoading,
      refresh,
      signInWithToken,
      signOut,
      setToken,
    }),
    [state, flags, isAuthenticated, isLoading, refresh, signInWithToken, signOut, setToken]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

export function useRoles(): Role[] {
  return useAuth().state.roles;
}